import React, { useState } from "react";

import {
  Backdrop,
  Button,
  Fade,
  Modal,
  Paper,
  Typography,
} from "@material-ui/core";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";

const NEW_PLATFORM_URL =
  "https://revue-fiduciaire.docebosaas.com/learn";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    modal: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: theme.spacing(2),
      zIndex: 3002,
    },
    backdrop: {
      backgroundColor: "rgba(24, 32, 47, 0.68)",
    },
    content: {
      boxSizing: "border-box",
      width: "100%",
      maxWidth: 720,
      maxHeight: "calc(100vh - 32px)",
      overflowY: "auto",
      padding: theme.spacing(5),
      borderRadius: 12,
      outline: 0,
      textAlign: "center",
      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(3),
      },
    },
    title: {
      marginBottom: theme.spacing(3),
      fontWeight: 700,
      fontSize: "1.75rem",
      lineHeight: 1.25,
      [theme.breakpoints.down("xs")]: {
        fontSize: "1.4rem",
      },
    },
    message: {
      marginBottom: theme.spacing(4),
      color: theme.palette.text.secondary,
      fontSize: "1rem",
      lineHeight: 1.65,
      [theme.breakpoints.down("xs")]: {
        marginBottom: theme.spacing(3),
        fontSize: "0.95rem",
      },
    },
    actions: {
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      "& > :not(:first-child)": {
        marginTop: theme.spacing(1.5),
      },
    },
    primaryButton: {
      minHeight: 50,
      padding: theme.spacing(1.25, 2),
      color: "#fff",
      fontWeight: 700,
      lineHeight: 1.35,
      textTransform: "none",
    },
    secondaryButton: {
      minHeight: 44,
      fontWeight: 600,
      textTransform: "none",
    },
  })
);

/**
 * Informs authenticated users that RF E-learning is moving to a new platform.
 * The dismissal is intentionally kept in component state only so the notice is
 * displayed again when the application is loaded after a new connection.
 */
const MigrationNoticeModal = () => {
  const classes = useStyles();
  const [open, setOpen] = useState(true);

  return (
    <Modal
      aria-labelledby="migration-notice-title"
      aria-describedby="migration-notice-description"
      className={classes.modal}
      open={open}
      disableEscapeKeyDown={true}
      closeAfterTransition={true}
      BackdropComponent={Backdrop}
      BackdropProps={{
        classes: { root: classes.backdrop },
        timeout: 250,
      }}
    >
      <Fade in={open} timeout={250}>
        <Paper
          className={classes.content}
          elevation={12}
          role="dialog"
          aria-modal="true"
        >
          <Typography
            id="migration-notice-title"
            className={classes.title}
            component="h1"
          >
            Votre expérience RF E-learning évolue
          </Typography>

          <Typography
            id="migration-notice-description"
            className={classes.message}
            component="p"
          >
            La plateforme sur laquelle vous êtes actuellement connecté n’est
            désormais plus alimentée en nouveaux contenus et sera prochainement
            déconnectée. Pour accéder aux nouvelles formations, aux contenus
            actualisés et aux nouvelles fonctionnalités de RF E-learning, nous
            vous invitons à vous connecter à la nouvelle plateforme.
          </Typography>

          <div className={classes.actions}>
            <Button
              className={classes.primaryButton}
              variant="contained"
              color="primary"
              href={NEW_PLATFORM_URL}
            >
              Accéder à la nouvelle plateforme RF E-learning
            </Button>
            <Button
              className={classes.secondaryButton}
              variant="text"
              onClick={() => setOpen(false)}
            >
              Continuer sur l’ancienne plateforme
            </Button>
          </div>
        </Paper>
      </Fade>
    </Modal>
  );
};

export default React.memo(MigrationNoticeModal);
